import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import apiClient from '../utils/api';
import API_CONFIG from '../config/api';
import { formatDate } from '../utils/dateFormatter';
import DOMPurify from 'dompurify';
import { AUTHOR, DISCLAIMER_TEXT, PROVENANCE_TEXT } from '../content/author';

const ArticleDetailPage = () => {
  // Get URL parameters - may include categorySlug and/or articleSlug depending on route
  const params = useParams();
  const categorySlug = params.categorySlug;
  const articleSlug = params.articleSlug || params.categorySlug; // articleSlug could be in either parameter depending on route

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchArticle = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      let apiUrl;
      if (categorySlug && params.articleSlug) {
        // This is a category-prefixed route: /categorySlug/articleSlug
        apiUrl = `/api/articles/${categorySlug}/${params.articleSlug}`;
      } else {
        // This is a core article route: /articleSlug
        apiUrl = `/api/articles/${articleSlug}`;
      }

      const response = await apiClient.get(apiUrl);
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
      if (error.response?.status === 404) {
        setError('Article not found.');
      } else {
        setError('Failed to load article. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [categorySlug, params.articleSlug, articleSlug]);

  useEffect(() => {
    fetchArticle();
  }, [fetchArticle]);

  // Update the tab title + meta description when an article loads. The server
  // already injects these into the initial HTML for crawlers (see
  // backend/services/seo_render.py); this keeps them correct during in-app
  // (client-side) navigation between articles, where no full page load occurs.
  useEffect(() => {
    if (article?.title) {
      document.title = `${article.title} — Vegetablah`;
    }
    return () => {
      // Restore the default title when leaving the article view.
      document.title = 'Vegetablah';
    };
  }, [article?.title]);

  useEffect(() => {
    if (article?.meta_description) {
      // Find or create meta description tag
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = article.meta_description;
    }
  }, [article?.meta_description]);

  const processedContent = useMemo(() => {
    if (!article?.content) return '';
    let content = article.content.replace(
      /src="\/media\//g,
      `src="${API_CONFIG.BASE_URL}/media/`
    );
    // Add not-prose class to visual element divs to prevent prose styles from affecting them
    // Match divs with visual element classes
    content = content.replace(
      /<div class="([^"]*)"([^>]*)>/g,
      (match, classAttr, rest) => {
        const visualClasses = [
          'info-card', 'warning-card', 'success-card', 'feature-card', 'quote-card', 'stats-card',
          'comparison-table', 'feature-grid', 'feature-item',
          'cta-primary', 'cta-secondary',
          'progress-steps', 'progress-step'
        ];

        const hasVisualClass = visualClasses.some(cls => classAttr.includes(cls));
        const hasRoundedBorder = classAttr.includes('rounded-lg') && classAttr.includes('border');

        if ((hasVisualClass || hasRoundedBorder) && !classAttr.includes('not-prose')) {
          return `<div class="${classAttr} not-prose"${rest}>`;
        }
        return match;
      }
    );
    return content;
  }, [article?.content]);

  const formattedDate = useMemo(() => {
    if (!article?.created_at) return { label: '', value: '' };

    const createdAt = new Date(article.created_at);
    const updatedAt = article.updated_at ? new Date(article.updated_at) : null;

    // If updated_at exists and is different from created_at, show updated date
    if (updatedAt && updatedAt.getTime() !== createdAt.getTime()) {
      return { label: 'Updated:', value: formatDate(article.updated_at) };
    }

    // Otherwise, show published date
    return { label: 'Published:', value: formatDate(article.created_at) };
  }, [article?.created_at, article?.updated_at]);

  const formattedCategories = useMemo(() => {
    if (!article?.categories || article.categories.length === 0) return '';

    const categoryNames = article.categories.map(cat => cat.name);
    return categoryNames.join(', ');
  }, [article?.categories]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <p className="text-fg-3">Loading article...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <p className="text-off-strong dark:text-off mb-4">{error}</p>
              <Link to="/articles">
                <Button>Back to Articles</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Article Content */}
      {!loading && !error && article && (
        <Card className="border-0">
          <CardContent className="py-8">
            <h1 className="text-4xl font-medium text-fg mb-4">
              {article.title}
            </h1>
            
            {/* Byline. Health content is YMYL, so the person behind it is named
                up front rather than left anonymous — the visible half of the
                same attribution the server injects for crawlers
                (backend/services/seo_render.py). */}
            <div className="mb-8 pb-4 border-b border-line">
              <p className="text-sm text-fg-2">
                By{' '}
                <Link
                  to={AUTHOR.path}
                  className="font-medium text-fg-2 underline"
                >
                  {AUTHOR.name}
                </Link>
                , {AUTHOR.role.toLowerCase()}
              </p>
              <div className="mt-1 flex flex-wrap gap-x-4 text-sm text-fg-3">
                {formattedDate.value && (
                  <span>
                    {formattedDate.label} {formattedDate.value}
                  </span>
                )}
                {formattedCategories && (
                  <span>
                    Category: {formattedCategories}
                  </span>
                )}
              </div>
            </div>

            <div
              className="prose prose-lg dark:prose-invert max-w-none prose-headings:text-fg dark:prose-headings:text-white prose-p:text-fg-2 dark:prose-p:text-fg-3 prose-a:text-blue-600 hover:prose-a:text-blue-800 dark:prose-a:text-blue-400 hover:dark:prose-a:text-blue-300 prose-a:no-underline"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processedContent, {
                ADD_ATTR: ['target', 'rel']
              }) }}
            />

            {/* Provenance + the wellness-not-medical line. Disclosed AI
                assistance is fine by Google's guidance; undisclosed is the
                risk — and a YMYL page owes the reader the disclaimer. */}
            <div className="mt-8 pt-6 border-t border-line space-y-2">
              <p className="text-sm text-fg-2">
                {PROVENANCE_TEXT}
              </p>
              <p className="text-sm text-fg-2">
                {DISCLAIMER_TEXT}
              </p>
            </div>

            {article.keywords && (
              <div className="mt-6 pt-6 border-t border-line">
                <p className="text-sm text-fg-3">
                  <span className="font-medium">Keywords:</span> {article.keywords}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ArticleDetailPage;

